import { useEffect, useCallback } from 'react'
import {
  RFI_TYPE_QUESTIONNAIRE,
  MANUFACTURING_TYPE_ID_PACKAGING,
  CAPABILITY_NAME,
  MESSAGE_VALIDATION_FAILED,
  RFI_TYPE_LOCATION,
  STATUS_INITIATED,
  CODE_RFI_TYPE_QUESTIONNAIRE,
} from '../../../../common/commonConstants'
import {
  getRfiTemplate,
  getMasterData,
  getRfi3Data,
  saveRfi3Data,
  getRFIAuditTrails,
  getApprovedCapabilities,
} from '../../../../services/lcmService'
import { buildServiceErrorsCache } from '../../../../common'
import { useRfi3Context } from './Rfi3Context'

export const useRfi3Data = () => {
  const { state, dispatch } = useRfi3Context()
  const {
    supplierId,
    locationId,
    manufacturingTypeId,
    rfi3Template,
    rfi3Data,
    rfiMasterData,
    isReadonly,
    isRfi3DataFetchProgress,
    isRfi3DataFetchFailed,
    errorCache,
    isShowOverlay,
    activeTab,
    auditData,
    capabilityOverview,
  } = state

  const prepareRFI3TemplateMetadata = useCallback((template) => {
    if (!template?.dynamic_sections?.tabs) return template

    const cloned = {
      ...template,
      dynamic_sections: {
        ...template.dynamic_sections,
        tabs: template.dynamic_sections.tabs.map((tab) => {
          const sections = tab.sections || []
          if (!sections.length) return tab
          const updatedSections = sections.map((section, index) => {
            const lastIndex = sections.length - 1
            return {
              ...section,
              ...(index === 0 ? { isBackVisible: false } : {}),
              ...(index === lastIndex ? { isNextVisible: false } : {}),
            }
          })
          return { ...tab, sections: updatedSections }
        }),
      },
    }

    return cloned
  }, [])

  const enrichRfi3DataWithCapabilityVettings = useCallback(
    (rfi3ServiceData, templateData, masterData) => {
      if (!templateData?.show_approval_popup) return rfi3ServiceData

      let capabilityMasterList = []

      if (manufacturingTypeId === MANUFACTURING_TYPE_ID_PACKAGING) {
        capabilityMasterList =
          masterData?.rfi_questionnaire?.packaging_capabilities?.map(
            (eachCapability) => {
              const currentStatus = (
                rfi3ServiceData.capability_vetting_status || []
              ).find(
                (eachExistingCapability) =>
                  eachExistingCapability.primary_type === eachCapability,
              )?.vetting_status

              return {
                type: CAPABILITY_NAME,
                primary_type: eachCapability,
                vetting_status: currentStatus,
              }
            },
          ) || []
      }

      return {
        ...rfi3ServiceData,
        capability_vetting_status: capabilityMasterList,
      }
    },
    [manufacturingTypeId],
  )

  const getAllRFI3Data = useCallback(async () => {
    if (!supplierId || !locationId || !manufacturingTypeId) return

    dispatch({ type: 'FETCH_START' })
    try {
      const [
        template,
        rfi3DataResp,
        masterData,
        auditData,
        capabilitiesOverview,
      ] = await Promise.all([
        getRfiTemplate(
          supplierId,
          RFI_TYPE_QUESTIONNAIRE,
          manufacturingTypeId,
          locationId,
          true,
        ),
        getRfi3Data(supplierId, locationId, manufacturingTypeId, true),
        getMasterData(RFI_TYPE_QUESTIONNAIRE, manufacturingTypeId),
        getRFIAuditTrails(
          RFI_TYPE_LOCATION,
          supplierId,
          locationId,
          manufacturingTypeId,
        ),
        isReadonly
          ? getApprovedCapabilities(
              RFI_TYPE_QUESTIONNAIRE,
              supplierId,
              locationId,
              manufacturingTypeId,
            ).catch(() => ({}))
          : {},
      ])

      const tabs = template?.dynamic_sections?.tabs || []
      if (tabs.length) {
        const firstTabIndex = 0
        const firstTab = tabs[firstTabIndex]

        dispatch({
          type: 'SET_ACTIVE_TAB',
          payload: {
            index: firstTabIndex,
            uid: firstTab.uid,
            name: firstTab.tab_name,
          },
        })

        const firstSection = firstTab?.sections?.[0]
        if (firstSection) {
          dispatch({
            type: 'SET_ACTIVE_SECTION',
            payload: {
              index: 0,
              uid: firstSection.uid,
              name: firstSection.section_name,
            },
          })
        }
      }

      const enrichedTemplate = prepareRFI3TemplateMetadata(template)
      dispatch({ type: 'SET_TEMPLATE', payload: enrichedTemplate })
      dispatch({ type: 'SET_MASTER_DATA', payload: masterData })

      const readonly =
        rfi3DataResp?.rfi3_read_only ||
        rfi3DataResp?.non_compliant ||
        rfi3DataResp?.archived
      dispatch({ type: 'SET_READONLY', payload: !!readonly })

      const enrichedRfi3Data = enrichRfi3DataWithCapabilityVettings(
        rfi3DataResp,
        template,
        masterData,
      )
      dispatch({ type: 'SET_DATA', payload: enrichedRfi3Data })

      dispatch({ type: 'SET_AUDIT_DATA', payload: auditData })
      const rfi3AuditTrail = (auditData || []).filter(
        (audit) =>
          (audit.rfi_string || '').toLowerCase() ===
          CODE_RFI_TYPE_QUESTIONNAIRE.toLowerCase(),
      )
      const lastRfi3Activity =
        rfi3AuditTrail[rfi3AuditTrail.length - 1]?.vetting_status
      dispatch({
        type: 'SET_FAST_TRACK_AVAILABLE',
        payload:
          lastRfi3Activity?.toLowerCase() === STATUS_INITIATED.toLowerCase(),
      })
      dispatch({
        type: 'SET_CAPABILITY_OVERVIEW',
        payload: capabilitiesOverview,
      })

      dispatch({ type: 'FETCH_SUCCESS' })
    } catch (error) {
      dispatch({ type: 'FETCH_FAILURE' })
    }
  }, [
    supplierId,
    locationId,
    manufacturingTypeId,
    dispatch,
    prepareRFI3TemplateMetadata,
    enrichRfi3DataWithCapabilityVettings,
    isReadonly,
  ])

  useEffect(() => {
    getAllRFI3Data()
  }, [getAllRFI3Data])

  const saveRfi3Payload = useCallback(
    async (sectionAnsPayload, sectionUid) => {
      if (!rfi3Data || !activeTab?.uid) return

      dispatch({ type: 'SAVE_START' })
      dispatch({ type: 'SET_OVERLAY', payload: true })

      try {
        const updatedSectionNode = {
          dynamic_sections: {
            [activeTab.uid]: {
              [sectionUid]: sectionAnsPayload,
            },
          },
        }

        const savePayload = {
          supplier_id: rfi3Data.supplier_id,
          location_id: rfi3Data.location_id,
          manufacturing_type: rfi3Data.manufacturing_type, // NOTE: for BE service, TODO remove when decom
          manufacturing_type_id: rfi3Data.manufacturing_type_id,
          ...updatedSectionNode,
        }

        const saveResponse = await saveRfi3Data(savePayload, true)

        const updatedRfi3Data = {
          ...rfi3Data,
          dynamic_sections: {
            ...rfi3Data.dynamic_sections,
            [activeTab.uid]: {
              ...rfi3Data.dynamic_sections[activeTab.uid],
              [sectionUid]: sectionAnsPayload,
            },
          },
          all_rfi3_answered: saveResponse.all_rfi3_answered,
        }

        dispatch({ type: 'SAVE_SUCCESS', payload: updatedRfi3Data })
        dispatch({ type: 'SET_ERROR_CACHE', payload: {} })
        dispatch({ type: 'SET_OVERLAY', payload: false })
      } catch (error) {
        const saveErrorCache = buildServiceErrorsCache(error)
        if (saveErrorCache) {
          dispatch({ type: 'SET_ERROR_CACHE', payload: saveErrorCache })
        }

        if (
          error?.response?.status === 400 &&
          error?.response?.data?.message?.toLowerCase() ===
            MESSAGE_VALIDATION_FAILED
        ) {
          const updatedRfi3DataForValidation = {
            ...rfi3Data,
            dynamic_sections: {
              ...rfi3Data.dynamic_sections,
              [activeTab.uid]: {
                ...rfi3Data.dynamic_sections[activeTab.uid],
                [sectionUid]: sectionAnsPayload,
              },
            },
          }
          dispatch({ type: 'SET_DATA', payload: updatedRfi3DataForValidation })
        }

        dispatch({ type: 'SAVE_FAILURE' })
        dispatch({ type: 'SET_OVERLAY', payload: false })
      }
    },
    [rfi3Data, activeTab, dispatch],
  )

  const setErrorCache = useCallback(
    (cache) => {
      dispatch({ type: 'SET_ERROR_CACHE', payload: cache })
    },
    [dispatch],
  )

  const setIsShowOverlay = useCallback(
    (flag) => {
      dispatch({ type: 'SET_OVERLAY', payload: flag })
    },
    [dispatch],
  )

  const isPageLoading = useCallback(
    () => isRfi3DataFetchProgress && !isRfi3DataFetchFailed,
    [isRfi3DataFetchProgress, isRfi3DataFetchFailed],
  )

  return {
    rfi3Template,
    rfi3Data,
    rfiMasterData,
    auditData,
    isReadonly,
    isRfi3DataFetchProgress,
    isRfi3DataFetchFailed,
    errorCache,
    capabilityOverview,
    setErrorCache,
    isShowOverlay,
    setIsShowOverlay,
    getAllRFI3Data,
    saveRfi3Payload,
    isPageLoading,
  }
}
